import React from 'react'
import PropTypes from 'prop-types'
import './DeadlineList.scss'

/**
 * DeadlineList - Upcoming deadlines display
 *
 * @param {Array} deadlines - Array of deadline objects
 * @param {string} title - List title
 * @param {number} maxItems - Maximum items to display
 * @param {boolean} showViewAll - Show "View All" link
 * @param {Function} onViewAll - View all callback
 * @param {Function} onDeadlineClick - Deadline click callback
 * @param {boolean} loading - Show loading state
 */
const DeadlineList = ({
	deadlines = [],
	title = 'Upcoming Deadlines',
	maxItems = 5,
	showViewAll = false,
	onViewAll,
	onDeadlineClick,
	loading = false
}) => {
	const getUrgencyLevel = (dueDate) => {
		if (!dueDate) return 'normal'

		const today = new Date()
		today.setHours(0, 0, 0, 0)
		const deadline = new Date(dueDate)
		deadline.setHours(0, 0, 0, 0)

		const diffDays = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24))

		if (diffDays < 0) return 'overdue'
		if (diffDays === 0) return 'today'
		if (diffDays <= 2) return 'urgent'
		if (diffDays <= 7) return 'warning'
		return 'normal'
	}

	const formatDueDate = (dueDate) => {
		if (!dueDate) return 'No date'

		const today = new Date()
		today.setHours(0, 0, 0, 0)
		const deadline = new Date(dueDate)
		deadline.setHours(0, 0, 0, 0)

		const diffDays = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24))

		if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`
		if (diffDays === 0) return 'Due today'
		if (diffDays === 1) return 'Due tomorrow'
		if (diffDays <= 7) return `Due in ${diffDays} days`

		return deadline.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric'
		})
	}

	const displayedDeadlines = deadlines.slice(0, maxItems)

	if (loading) {
		return (
			<div className="deadline-list">
				<div className="deadline-list__header">
					<h3 className="deadline-list__title">{title}</h3>
				</div>
				<div className="deadline-list__items">
					{[...Array(3)].map((_, i) => (
						<div key={i} className="deadline-list__item deadline-list__item--skeleton">
							<div className="skeleton skeleton--date"></div>
							<div className="skeleton skeleton--content">
								<div className="skeleton skeleton--name"></div>
								<div className="skeleton skeleton--project"></div>
							</div>
						</div>
					))}
				</div>
			</div>
		)
	}

	return (
		<div className="deadline-list">
			<div className="deadline-list__header">
				<h3 className="deadline-list__title">{title}</h3>
				{showViewAll && onViewAll && (
					<button className="deadline-list__view-all" onClick={onViewAll}>
						View All
					</button>
				)}
			</div>

			{displayedDeadlines.length === 0 ? (
				<div className="deadline-list__empty">
					<svg viewBox="0 0 24 24" fill="currentColor" className="deadline-list__empty-icon">
						<path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
					</svg>
					<p>No upcoming deadlines</p>
				</div>
			) : (
				<div className="deadline-list__items">
					{displayedDeadlines.map((deadline, index) => {
						const urgency = getUrgencyLevel(deadline.due_date)
						return (
							<div
								key={deadline.id || index}
								className={`deadline-list__item deadline-list__item--${urgency}`}
								onClick={() => onDeadlineClick && onDeadlineClick(deadline)}
								role={onDeadlineClick ? 'button' : undefined}
								tabIndex={onDeadlineClick ? 0 : undefined}
							>
								<div className="deadline-list__date-badge">
									{deadline.due_date ? (
										<>
											<span className="deadline-list__date-day">
												{new Date(deadline.due_date).getDate()}
											</span>
											<span className="deadline-list__date-month">
												{new Date(deadline.due_date).toLocaleDateString('en-US', { month: 'short' })}
											</span>
										</>
									) : (
										<>
											<span className="deadline-list__date-day">--</span>
											<span className="deadline-list__date-month">N/A</span>
										</>
									)}
								</div>

								<div className="deadline-list__content">
									<p className="deadline-list__name">{deadline.title || deadline.name}</p>
									{deadline.project && (
										<span className="deadline-list__project">{deadline.project}</span>
									)}
									<span className="deadline-list__due-text">
										{formatDueDate(deadline.due_date)}
									</span>
								</div>

								{urgency === 'overdue' && (
									<span className="deadline-list__badge deadline-list__badge--overdue">
										Overdue
									</span>
								)}
								{urgency === 'today' && (
									<span className="deadline-list__badge deadline-list__badge--today">
										Today
									</span>
								)}
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

DeadlineList.propTypes = {
	deadlines: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			title: PropTypes.string,
			name: PropTypes.string,
			due_date: PropTypes.string,
			project: PropTypes.string
		})
	),
	title: PropTypes.string,
	maxItems: PropTypes.number,
	showViewAll: PropTypes.bool,
	onViewAll: PropTypes.func,
	onDeadlineClick: PropTypes.func,
	loading: PropTypes.bool
}

export default DeadlineList
